/**
 * A street map in the page's own code (Delivery D11): Web-Mercator tiles from the organisation's configured street
 * provider (Delivery › Maps and API Keys), drag to pan, wheel or buttons to zoom, and whatever the screen draws on top
 * as SVG through `project`. No map library: the office's map needs tiles, lines and dots, and nothing more.
 *
 * The slots come in order — primary, backup, alternate — and a tile that fails moves the whole map to the next slot, as
 * the rider app does (ADR-0034). With no slot configured the map is a plain grid, and says so; lines and dots still draw.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Minus, Plus } from 'lucide-react';

export interface TileSlot { slot: string; vendor: string; url: string; attribution?: string | null; tileSize?: number | null; key?: string | null }
export interface LatLng { lat: number; lng: number }
export interface MapView { center: LatLng; zoom: number }
type Project = (p: LatLng) => { x: number; y: number };

const TILE = 256;
const clampZoom = (z: number) => Math.max(3, Math.min(19, z));
const world = (z: number) => TILE * 2 ** z;
function toPixel(p: LatLng, z: number): { x: number; y: number } {
  const s = Math.sin((Math.max(-85, Math.min(85, p.lat)) * Math.PI) / 180);
  return { x: ((p.lng + 180) / 360) * world(z), y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * world(z) };
}
function toLatLng(x: number, y: number, z: number): LatLng {
  const n = Math.PI - (2 * Math.PI * y) / world(z);
  return { lat: (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))), lng: (x / world(z)) * 360 - 180 };
}
function tileUrl(slot: TileSlot, z: number, x: number, y: number): string {
  return slot.url.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y))
    .replace('{key}', encodeURIComponent(slot.key ?? '')).replace('{s}', 'a').replace('{r}', '');
}

/** Metres to pixels at a latitude and zoom (Web Mercator), for a geofence or accuracy circle. */
export const metresToPx = (m: number, lat: number, zoom: number) => m / ((156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom);

/** The view that shows every point, or the fallback when there are none. */
export function fitView(points: LatLng[], width: number, height: number, fallback: MapView): MapView {
  if (points.length === 0) return fallback;
  if (points.length === 1) return { center: points[0]!, zoom: 15 };
  const lats = points.map((p) => p.lat); const lngs = points.map((p) => p.lng);
  const box = { n: Math.max(...lats), s: Math.min(...lats), e: Math.max(...lngs), w: Math.min(...lngs) };
  let zoom = 18;
  while (zoom > 3) {
    const a = toPixel({ lat: box.n, lng: box.w }, zoom); const b = toPixel({ lat: box.s, lng: box.e }, zoom);
    if (Math.abs(b.x - a.x) < width * 0.85 && Math.abs(b.y - a.y) < height * 0.85) break;
    zoom -= 1;
  }
  return { center: { lat: (box.n + box.s) / 2, lng: (box.e + box.w) / 2 }, zoom };
}

export function TileMap({ slots, view, onView, height = 560, emptyNote, overlay, children, still = false }: {
  slots: TileSlot[]; view: MapView; onView: (v: MapView) => void; height?: number | string; emptyNote?: string;
  /** A picture of the map: no dragging, no wheel zoom, no zoom buttons (a printed report). */
  still?: boolean;
  /** HTML drawn over the map (panels, buttons), marked data-map-overlay; it takes its own pointer events. */
  overlay?: ReactNode;
  children?: (project: Project, zoom: number) => ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [boxHeight, setBoxHeight] = useState(typeof height === 'number' ? height : 560);
  const [slotIndex, setSlotIndex] = useState(0);
  const drag = useRef<{ x: number; y: number; cx: number; cy: number; moved: boolean } | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => { setWidth(el.clientWidth || 800); setBoxHeight(el.clientHeight || 560); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => setSlotIndex(0), [slots]);
  const slot = slots[slotIndex];
  const centre = toPixel(view.center, view.zoom);
  const tall = boxHeight;
  const origin = { x: centre.x - width / 2, y: centre.y - tall / 2 };
  const project: Project = (p) => { const q = toPixel(p, view.zoom); return { x: q.x - origin.x, y: q.y - origin.y }; };
  const tiles = useMemo(() => {
    const out: { key: string; x: number; y: number; left: number; top: number }[] = [];
    const n = 2 ** view.zoom;
    for (let tx = Math.floor(origin.x / TILE); tx <= Math.floor((origin.x + width) / TILE); tx += 1) {
      for (let ty = Math.floor(origin.y / TILE); ty <= Math.floor((origin.y + tall) / TILE); ty += 1) {
        if (ty < 0 || ty >= n) continue;
        out.push({ key: `${view.zoom}/${tx}/${ty}`, x: ((tx % n) + n) % n, y: ty, left: tx * TILE - origin.x, top: ty * TILE - origin.y });
      }
    }
    return out;
  }, [origin.x, origin.y, width, tall, view.zoom]);
  const zoomAt = (dz: number, px = width / 2, py = tall / 2) => {
    const z = clampZoom(view.zoom + dz);
    if (z === view.zoom) return;
    const at = toLatLng(origin.x + px, origin.y + py, view.zoom);
    const after = toPixel(at, z);
    onView({ zoom: z, center: toLatLng(after.x - px + width / 2, after.y - py + tall / 2, z) });
  };

  return (
    <div ref={box} className="relative select-none overflow-hidden rounded-md border border-line bg-[#e8eaed]" style={{ height }}
         onPointerDown={still ? undefined : (e) => {
           // The panels over the map keep their own clicks, typing and scrolling.
           if ((e.target as Element).closest('[data-map-overlay]')) return;
           drag.current = { x: e.clientX, y: e.clientY, cx: centre.x, cy: centre.y, moved: false };
         }}
         onPointerMove={still ? undefined : (e) => {
           const d = drag.current; if (!d) return;
           // A pin is a click until the pointer really moves; only then does the map take the drag.
           if (!d.moved && Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) < 4) return;
           if (!d.moved) (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
           d.moved = true;
           onView({ zoom: view.zoom, center: toLatLng(d.cx - (e.clientX - d.x), d.cy - (e.clientY - d.y), view.zoom) });
         }}
         onPointerUp={still ? undefined : () => { drag.current = null; }}
         onWheel={still ? undefined : (e) => { if ((e.target as Element).closest('[data-map-overlay]')) return; const r = box.current!.getBoundingClientRect(); zoomAt(e.deltaY < 0 ? 1 : -1, e.clientX - r.left, e.clientY - r.top); }}
         role={still ? 'img' : 'application'} aria-label="Map">
      {slot
        ? tiles.map((t) => (
            <img key={`${slotIndex}:${t.key}`} src={tileUrl(slot, view.zoom, t.x, t.y)} alt="" draggable={false} width={TILE} height={TILE}
                 className="absolute" style={{ left: t.left, top: t.top }}
                 onError={() => setSlotIndex((i) => (i === slotIndex && i + 1 < slots.length ? i + 1 : i))} />
          ))
        : <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(#d5d8dc 1px, transparent 1px), linear-gradient(90deg, #d5d8dc 1px, transparent 1px)', backgroundSize: '64px 64px' }} />}
      <svg className="absolute inset-0" width={width} height={tall} style={{ pointerEvents: 'none' }}>{children?.(project, view.zoom)}</svg>
      {still ? null : <div data-map-overlay className="absolute bottom-8 right-2 flex flex-col overflow-hidden rounded border border-line bg-surface shadow-sm">
        <button type="button" className="p-1.5 hover:bg-surface-sunken" aria-label="Zoom in" onClick={(e) => { e.stopPropagation(); zoomAt(1); }}><Plus size={16} /></button>
        <button type="button" className="border-t border-line p-1.5 hover:bg-surface-sunken" aria-label="Zoom out" onClick={(e) => { e.stopPropagation(); zoomAt(-1); }}><Minus size={16} /></button>
      </div>}
      {overlay}
      <div className="absolute bottom-0 right-0 bg-surface/80 px-1.5 py-0.5 text-[11px] text-ink-muted">
        {slot ? `${slot.attribution ?? slot.vendor}${slotIndex > 0 ? ` · ${slot.slot}` : ''}` : emptyNote ?? 'No street map is configured (Delivery › Maps and API Keys) — the lines and dots are still true'}
      </div>
    </div>
  );
}
